import type { Shop, Collection } from '@prisma/client'
import type { SerializeFrom } from '@remix-run/node'

import { makeTitle } from '~/helpers/pages'
import { makeMainPath, makeShopPath } from '~/helpers/routes'
import { getEntireStoreDiscount } from '~/helpers/shop'
import type { CollectionItemWithProduct } from '~/models/collection_item.server'
import type { ProductFull } from '~/models/product.server'

import type { TransformationOptions } from './image'
import { getImageKitUrl, convertTransformationToString } from './image'

// Schema.org availability enum
enum SchemaAvailability {
  InStock = 'https://schema.org/InStock',
  OutOfStock = 'https://schema.org/OutOfStock',
  PreOrder = 'https://schema.org/PreOrder',
}

export type ProductSeoData = {
  _type: 'Product'
  name: string
  description: string
  image: string
  sku: string
  url: string
  brand?: {
    _type: 'Brand'
    name: string
  }
  offers: {
    _type: 'Offer'
    price: number
    priceCurrency: string
    availability: SchemaAvailability
    url: string
    seller?: {
      _type: 'Organization'
      name: string
    }
  }
  category?: string
  additionalProperty?: Array<{
    _type: 'PropertyValue'
    name: string
    value: string
  }>
}

export type CollectionSeoData = {
  _type: 'CollectionPage'
  name: string
  url: string
  description: string
  image?: string
  mainEntity: {
    _type: 'ItemList'
    itemListElement: Array<{
      _type: 'ListItem'
      position: number
      item: {
        _type: 'Product'
        name: string
        url: string
        image?: string
      }
    }>
  }
}

export type StoreSeoData = {
  _type: 'WebSite'
  name: string
  url: string
  description: string
  image?: string
  inLanguage?: string
  publisher: {
    _type: 'Organization'
    name: string
    logo?: string
    address?: {
      _type: 'PostalAddress'
      streetAddress: string
      addressLocality: string
      postalCode: string
      addressCountry: string
    }
    contactPoint?: {
      _type: 'ContactPoint'
      contactType: 'customer service'
      email: string
    }
    sameAs?: string[] // social media URLs
  }
}

export const generateSeoImage = (
  imagePath: string | undefined | null,
  options?: TransformationOptions
) => {
  if (!imagePath) {
    return ''
  }

  const transformationOptions = {
    width: options?.width || 800,
    height: options?.height,
    quality: options?.quality || 70,
    format: 'jpeg' as const,
    resizeMode: options?.resizeMode || ('fill' as const),
  }

  const transformationString = convertTransformationToString(
    transformationOptions
  )

  return getImageKitUrl(imagePath, { transformation: transformationString })
}

export const generateProductSeoData = (
  product: ProductFull,
  shop: Shop
): ProductSeoData => {
  const lowestPriceVariant = product.variants
    .filter((v) => v.isVisible && v.stock > 0)
    .sort((a, b) => a.price - b.price)[0]

  // Apply store discount if available
  const discount = getEntireStoreDiscount(shop) || 0
  const currentPriceInCents = lowestPriceVariant
    ? lowestPriceVariant.price - (lowestPriceVariant.price * discount) / 100
    : 0

  const currentPriceInEuros = currentPriceInCents / 100

  const availability = product.isPresale
    ? SchemaAvailability.PreOrder
    : (lowestPriceVariant?.stock || 0) > 0
      ? SchemaAvailability.InStock
      : SchemaAvailability.OutOfStock

  const getFirstImage = () => {
    const attachment = product.attachments.find((a) => a.media.kind === 'image')

    if (!attachment) {
      return ''
    }

    return generateSeoImage(attachment.media.src)
  }

  const productUrl = makeShopPath(
    shop.slug,
    `/products/${product.id}/${product.slug}`
  )

  return {
    _type: 'Product',
    name: product.name,
    description:
      product.descriptionPrimary || product.descriptionSecondary || '',
    image: getFirstImage(),
    sku: product.sku,
    url: productUrl,
    brand: {
      _type: 'Brand',
      name: shop.name,
    },
    offers: {
      _type: 'Offer',
      price: currentPriceInEuros,
      priceCurrency: 'EUR',
      availability,
      url: productUrl,
      seller: {
        _type: 'Organization',
        name: shop.name,
      },
    },
  }
}

export const generateCollectionSeoData = (
  collection: Collection,
  collectionItems: CollectionItemWithProduct[],
  shop: Shop
): CollectionSeoData => {
  const publishedItems = collectionItems.filter(
    (item) =>
      item.product && !item.product.archivedAt && item.product.publishedAt
  )

  const collectionUrl = makeShopPath(
    shop.slug,
    `/collections/${collection.id}/${collection.slug}`
  )

  return {
    _type: 'CollectionPage',
    name: collection.name,
    url: collectionUrl,
    description: collection.description || '',
    image: generateSeoImage(collection.avatarSrc),
    mainEntity: {
      _type: 'ItemList',
      itemListElement: publishedItems.map((item, index) => ({
        _type: 'ListItem',
        position: index + 1,
        item: {
          _type: 'Product',
          name: item.product!.name,
          url: collectionUrl,
        },
      })),
    },
  }
}

export const generateStoreSeoData = (shop: Shop): StoreSeoData => {
  const address =
    shop.addressLine && shop.addressCity && shop.addressCountry
      ? {
          _type: 'PostalAddress' as const,
          streetAddress: shop.addressLine,
          addressLocality: shop.addressCity,
          postalCode: shop.addressPostcode || '',
          addressCountry: shop.addressCountry,
        }
      : undefined

  const contactPoint = shop.supportEmail
    ? {
        _type: 'ContactPoint' as const,
        contactType: 'customer service' as const,
        email: shop.supportEmail,
      }
    : undefined

  const sameAs = []
  if (shop.instagramHandle) {
    sameAs.push(`https://instagram.com/${shop.instagramHandle}`)
  }

  const defaultAvatarSrc = makeMainPath(`/favicon.png`)

  return {
    _type: 'WebSite',
    name: shop.name,
    url: makeShopPath(shop.slug, '/'),
    description: shop.description || '',
    image: generateSeoImage(shop.avatarSrc) || defaultAvatarSrc,
    inLanguage: 'en',
    publisher: {
      _type: 'Organization',
      name: shop.name,
      logo:
        generateSeoImage(shop.avatarSrc, {
          width: 192,
          height: 192,
          resizeMode: 'crop',
        }) || defaultAvatarSrc,
      ...(address && { address }),
      ...(contactPoint && { contactPoint }),
      ...(sameAs.length > 0 && { sameAs }),
    },
  }
}

export type SeoMetaConfig = {
  title?: string
  seoTitle?: string // for og:title and twitter:title
  description?: string
  image?: string
  canonicalUrl?: string
  ogType?: string
  ogUrl?: string
  twitterImage?: string
}

export const generateSeoMetaTags = (
  overrides: SeoMetaConfig = {},
  shop: Shop | SerializeFrom<Shop>,
  faviconPng?: string,
  faviconIco?: string
) => {
  // Default values
  const defaults = {
    title: makeTitle(shop.name),
    seoTitle: shop.name,
    description: shop.description || `${shop.name} - Online Store`,
    image: generateSeoImage(shop.avatarSrc) || makeMainPath(`/gradient.png`),
    canonicalUrl: makeShopPath(shop.slug, '/'),
    ogType: 'website',
    ogUrl: makeShopPath(shop.slug, '/'),
    twitterImage:
      generateSeoImage(shop.avatarSrc) || makeMainPath(`/gradient.png`),
  }

  // Merge overrides with defaults
  const config = { ...defaults, ...overrides }

  // Use provided favicon or fallback to default
  const shopFaviconPng = faviconPng || makeMainPath(`/favicon.png`)
  const shopFaviconIco = faviconIco || makeMainPath(`/favicon.ico`)

  return [
    { title: config.title },
    { name: 'description', content: config.description },

    // Favicon - Prioritize shop branding, with ICO fallback for older browsers
    { tagName: 'link', rel: 'icon', type: 'image/png', href: shopFaviconPng },
    {
      tagName: 'link',
      rel: 'shortcut icon',
      type: 'image/png',
      href: shopFaviconPng,
    },
    { tagName: 'link', rel: 'apple-touch-icon', href: shopFaviconPng },
    // Fallback ICO for older browsers that don't support PNG
    {
      tagName: 'link',
      rel: 'icon',
      type: 'image/x-icon',
      href: shopFaviconIco,
    },

    // Open Graph / Facebook
    { property: 'og:type', content: config.ogType },
    { property: 'og:title', content: config.seoTitle },
    { property: 'og:description', content: config.description },
    { property: 'og:image', content: config.image },
    { property: 'og:site_name', content: 'Protocol' },
    ...(config.ogUrl ? [{ property: 'og:url', content: config.ogUrl }] : []),

    // Twitter
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: config.seoTitle },
    { name: 'twitter:description', content: config.description },
    { name: 'twitter:image', content: config.twitterImage },

    // Canonical URL
    ...(config.canonicalUrl
      ? [{ tagName: 'link', rel: 'canonical', href: config.canonicalUrl }]
      : []),
  ]
}
